/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

interface CDSAction {
    name: string;
    needsReason?: boolean;
    action?: (reason: string) => void;
    returns?: unknown;
}
export interface CDSData {
    title: string;
    msg: string;
    warning?: boolean;
    needsReason?: boolean;
    asHtml?: boolean;
    keepLineBreaks?: boolean;
    options: CDSAction[];
}

/**
 * @title Dialog with header, scrollable content and actions
 */
@Injectable({
    providedIn: 'root'
})
export class ConfirmDialogService {
    protected dialog = inject(MatDialog);

    cancelOption = { name: $localize`Cancel`, action: () => { console.log('Cancelled action'); }, returns: null }
    okOption = { name: $localize`OK`, action: () => { console.log('Cancelled action'); }, returns: 'OK' }

    openConfirm(title: string, msg: string) {
        const options = {
            title, msg, options: [this.okOption, this.cancelOption]
        }
        return this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: options }).afterClosed();
    }

    openChoice(options: CDSData, withCancel = true) {
        if (withCancel) {
            options.options.push(this.cancelOption);
        }

        return this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: options }).afterClosed();
    }

    open(title: string, msg: string, action: (_reason?:string) => void, actionName: string = $localize`OK`, needsReason = false) {
        const dataOptions = {
            title, msg, needsReason,
            options: [
                { name: actionName, action, needsReason },
                this.cancelOption,
            ]
        };
        return this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: dataOptions }).afterClosed();
    }

    alert(title: string, msg: string, action: () => void = null, asHtml = false) {
        const okOption = this.okOption;
        if (action) {
            okOption.action = action;
        }
        const dataOptions = {
            title, msg, asHtml,
            options: [
                okOption,
            ]
        };
        return this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: dataOptions });
    }

    showLongText(title: string, msg: string) {
        const dataOptions = { title, msg, keepLineBreaks: true, options: [this.okOption] };
        return this.dialog.open(ConfirmDialogComponent, { data: dataOptions, width: '80vw', maxWidth: '1000px' });
    }
}

@Component({
    selector: 'app-confirm-dialog',
    template: `<div class="confirmDialog" style="padding-bottom:10px"><div style="padding: 10px">
      <h4 mat-dialog-title>{{data.title}}</h4>
      <div mat-dialog-content>
        <div style="display:flex; align-items: center">
            @if(data.warning) {
                <mat-icon color="accent" style="font-size: 72px;width:72px;height:72px">warning</mat-icon>
            }
            @if (data.asHtml) {
                <div [innerHtml]="data.msg"></div>
            } @else {
                <p [style.white-space]="data.keepLineBreaks ? 'pre-wrap' : null">{{data.msg}}</p>
            }
        </div>
        @if (data.needsReason) {
            <textarea [(ngModel)]="reason" placeholder="Reason" rows="4" style="width:100%"></textarea>
        }
      </div>
      <div mat-dialog-actions align="end">
        @for (option of data.options; track option) {
          <button mat-button [disabled]=optionDisabled(option) [attr.data-confirm-option]="option.name"
          [attr.data-confirm-index]="$index"
          (click)="clickAction(option)">{{option.name}}</button>
        }
      </div></div></div>`,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatButtonModule,
        FormsModule,
        MatIconModule,
    ]
})

export class ConfirmDialogComponent {
    dialogRef = inject<MatDialogRef<ConfirmDialogComponent, unknown>>(MatDialogRef);
    protected dialog = inject(MatDialog);
    data = inject<CDSData>(MAT_DIALOG_DATA);


    reason = '';

    optionDisabled(option: CDSAction) {
        if (option.needsReason && this.reason.length < 5) {
            return true;
        }
        return false;
    }

    closeMe(): void {
        this.dialogRef.close();
    }

    clickAction(option: CDSAction) {
        if (option.action) {
            option.action(this.reason);
        }
        this.dialogRef.close(option.returns);
    }
}
